import { getDuration, getWatchId, getWatchLocation } from 'utils/helpers';
import { isId } from 'utils/is-id';
import { VIEWABLE_TYPES } from 'utils/constants';
import getViewableQuery from './viewable.gql';
import getViewableCustomIdQuery from './viewableCustomId.gql';

export function getPageTitle(viewable) {
  if (viewable.broadcastById) {
    return viewable.broadcastById.title || viewable.title;
  }

  return viewable.title;
}

export function getPageDescription(viewable) {
  if (viewable.broadcastById) {
    return viewable.broadcastById.description || viewable.channelDescription;
  }
  // channel description as main. Schedule description as fallback
  return viewable.channelDescription || viewable.description;
}

export function getCanonicalData(location, id, isShow) {
  const params = isShow ? { id: location.params.id } : location.params;

  if (id) {
    params.id = id;
  }

  return {
    name: location.name,
    params,
    query: null,
  };
}

export function getNextRecommendation(viewable) {
  if (viewable.seasons?.length) { // next recommendation only for last episode-season
    const lastSeason = viewable.seasons[viewable.seasons.length - 1];
    if (lastSeason?.seasonNumber !== viewable.seasonNumber) return;

    const lastEpisode = lastSeason.episodes[lastSeason.episodes - 1];
    if (lastEpisode?.episodeNumber !== viewable.episodeNumber) return;
  }

  return viewable.playNext?.[0];
}

const typeMap = {
  Show: 'TVSeries',
  Episode: 'TVEpisode',
  Movie: 'Movie',
  Channel: 'BroadcastService',
  Program: 'BroadcastEvent',
};

/**
 * Convert seconds to ISO 8601 duration (H/M/S only).
 *
 * Examples:
 *   durationToISO(3661)      -> "PT1H1M1S"
 *   durationToISO(3661.5)    -> "PT1H1M1.5S"
 *   durationToISO(61)        -> "PT1M1S"
 *   durationToISO(0)         -> "PT0S"
 *   durationToISO(-90.25)    -> "-PT1M30.25S"
 *
 * @param {number} sec - duration in seconds (can be fractional/negative)
 * @returns {string} ISO 8601 duration string
 */
function durationToISO(sec) {
  if (typeof sec !== 'number' || !isFinite(sec)) {
    return null;
  }

  const negative = sec < 0;
  const totalSeconds = Math.abs(sec);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  let seconds = totalSeconds % 60;

  // Round to milliseconds precision to avoid float noise
  seconds = Math.round(seconds * 1000) / 1000;

  function fmtSeconds(s) {
    if (Math.abs(s - Math.round(s)) < 1e-9) return String(Math.round(s));
    return s.toFixed(3).replace(/\.?0+$/, '');
  }

  let out = 'PT';
  if (hours) out += `${hours}H`;
  if (minutes) out += `${minutes}M`;
  if (seconds || (!hours && !minutes && seconds === 0)) {
    out += `${fmtSeconds(seconds)}S`;
  }

  return negative ? `-${out}` : out;
}

// Viewable to Json Schema https://schema.org/
// https://magine.atlassian.net/browse/MDM-23375
export function mapViewableToSchema(viewable, router) {
  const isShow = viewable.__typename === VIEWABLE_TYPES.Show;

  const url = router ? router.getUrl(
    getWatchLocation(getWatchId(viewable), viewable?.broadcastById?.broadcastId),
  ) : null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': typeMap[viewable.__typename],
    name: viewable.title || null,
    description: viewable.description || null,
    image: viewable.poster || null,
    url,
    datePublished: viewable.productionYear || null,
    contentRating: viewable.contentRating?.name || viewable.rating || null,
    duration: isShow ? null : durationToISO(getDuration(viewable)) || null, // series usually don’t have total duration
    genre: viewable.genres || [],
    actor: viewable.tagsToRender
      ?.find(tag => tag.translationKey === 'DetailViewMetadataCast')
      ?.values?.map(actorName => ({
        '@type': 'Person',
        name: actorName,
      })) || [],
    director: viewable.tagsToRender
      ?.find(tag => tag.translationKey === 'DetailViewMetadataDirectors')
      ?.values?.map(dirName => ({
        '@type': 'Person',
        name: dirName,
      })) || [],
    offers: viewable.offers
      ? viewable.offers
        .filter(o => o && o.priceInCents)
        .map(o => ({
          '@type': 'Offer',
          name: o.title,
          price: (o.priceInCents / 100).toString(),
          priceCurrency: o.currency || null,
          availability: 'https://schema.org/InStock',
        }))
      : [],
  };

  if (viewable.__typename === VIEWABLE_TYPES.Episode) {
    schema.episodeNumber = viewable.episodeNumber;
  }

  if (isShow && viewable.seasons?.length) {
    schema.containsSeason = viewable.seasons.map(season => ({
      '@type': 'TVSeason',
      seasonNumber: season.seasonNumber,
      name: `Season ${season.seasonNumber}`,
      episode: season.episodes?.map(e => mapViewableToSchema(e)) || [],
    }));
  }

  if (viewable.__typename === VIEWABLE_TYPES.Channel) {
    schema.image = viewable.logoLight || viewable.logoDark || null;
    schema.description = viewable.channelDescription || viewable.description || null;

    if (viewable.broadcastById) {
      const program = viewable.broadcastById;
      schema.broadcastEvent = {
        '@type': 'BroadcastEvent',
        name: program.title,
        description: program.description,
        startDate: new Date(program.start * 1000).toISOString(),
        endDate: new Date(program.stop * 1000).toISOString(),
        isLiveBroadcast: false,
        image: program.poster || program.banner || null,
        video: {
          '@type': 'VideoObject',
          name: program.title,
          thumbnailUrl: program.poster || null,
          uploadDate: program.catchup ? new Date(program.catchup.from * 1000).toISOString() : null,
          expires: program.catchup ? new Date(program.catchup.to * 1000).toISOString() : null,
        },
      };
    } else {
      schema.broadcastEvent = viewable.schedule?.map(program => ({
        '@type': 'BroadcastEvent',
        name: program.title,
        description: program.description,
        startDate: new Date(program.start * 1000).toISOString(),
        endDate: new Date(program.stop * 1000).toISOString(),
        isLiveBroadcast: program.liveAvailable || false,
        image: program.poster || program.banner || null,
      })) || [];
    }
  }

  if (viewable.__typename === VIEWABLE_TYPES.Program) {
    return {
      ...schema,
      startDate: viewable.defaultPlayable?.startTimeUtc
        ? new Date(viewable.defaultPlayable.startTimeUtc * 1000).toISOString()
        : null,
      endDate: viewable.defaultPlayable?.stopTimeUtc
        ? new Date(viewable.defaultPlayable.stopTimeUtc * 1000).toISOString()
        : null,
      isLiveBroadcast: false,
      video: {
        '@type': 'VideoObject',
        name: viewable.title || 'Program',
        thumbnailUrl: viewable.poster || null,
        duration: durationToISO(getDuration(viewable)) || null,
        uploadDate: viewable.defaultPlayable?.catchup?.from
          ? new Date(viewable.defaultPlayable.catchup.from * 1000).toISOString()
          : null,
        expires: viewable.defaultPlayable?.catchup?.to
          ? new Date(viewable.defaultPlayable.catchup.to * 1000).toISOString()
          : null,
      },
    };
  }

  return schema;
}

export function getApolloOptions(params) {
  return {
    skip: !params.id,
    query: isId(params.id) ? getViewableQuery : getViewableCustomIdQuery,
    errorPolicy: 'ignore',
    variables: {
      viewableId: params.id,
      broadcastId: params.playableId || '',
    },
  };
}
